<template>
  <base-modal
    ref="core"
    size="xl"
    dialog-style="width: 90vw; max-width: 1400px"
    :prevent-close="importingJobId !== null"
  >
    <template #header>
      <div>
        <span class="tfa-eyebrow">{{ $t('components.tachoAnalysis.remoteFiles.eyebrow') }}</span>
        <h4 class="tfa-page-title">
          {{ $t('components.tachoAnalysis.remoteFiles.title') }}: {{ driverName }}
        </h4>
        <p class="tfa-page-sub">
          {{ $t('components.tachoAnalysis.remoteFiles.sub') }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="tfa-remote-body">
        <div
          class="tfa-remote-status"
          :class="{ 'is-error': !!error }"
        >
          <template v-if="error">
            {{ error }}
          </template>
          <template v-else-if="totalCount === null">
            <span class="spinner-border spinner-border-sm" />
            {{ $t('components.tachoAnalysis.remoteFiles.loading') }}
          </template>
          <template v-else-if="!totalCount">
            {{ $t('components.tachoAnalysis.remoteFiles.empty') }}
          </template>
          <template v-else>
            {{ filesSummary(totalCount) }}
          </template>
        </div>

        <DxDataGrid
          ref="grid"
          class="tfa-remote-grid"
          :column-auto-width="true"
          :data-source="candidatesStore"
          :hover-state-enabled="true"
          :load-panel="{ enabled: false }"
          :no-data-text="$t('components.tachoAnalysis.remoteFiles.empty')"
          :remote-operations="{ paging: true, sorting: true, filtering: true }"
          :row-alternation-enabled="true"
          :show-borders="false"
          :show-column-lines="false"
        >
          <DxFilterRow :visible="true" />
          <DxPaging :page-size="10" />
          <DxPager
            :visible="(totalCount ?? 0) > 10"
            :show-info="true"
            :show-navigation-buttons="true"
            :info-text="$t('components.devexpress.pagerPage')
              + '{0}' + $t('components.devexpress.pagerOf')
              + '{1}'"
          />
          <DxColumn
            :allow-filtering="false"
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.driver')"
            :min-width="175"
            data-field="card_number"
            cell-template="driver-cell"
          />
          <DxColumn
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.file')"
            :min-width="220"
            data-field="filename"
            cell-template="file-cell"
          />
          <DxColumn
            :allow-filtering="false"
            :allow-sorting="false"
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.range')"
            :min-width="195"
            cell-template="range-cell"
          />
          <DxColumn
            :allow-filtering="false"
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.downloaded')"
            :min-width="145"
            data-field="job_completed_at"
            sort-order="desc"
            cell-template="downloaded-cell"
          />
          <DxColumn
            :allow-filtering="false"
            :allow-sorting="false"
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.state')"
            css-class="tfa-remote-state-cell"
            :width="285"
            cell-template="state-cell"
          />
          <DxColumn
            :allow-filtering="false"
            :allow-sorting="false"
            :caption="$t('components.tachoAnalysis.remoteFiles.cols.actions')"
            css-class="tfa-remote-actions-cell"
            :width="135"
            cell-template="actions-cell"
          />

          <template #driver-cell="{ data }">
            <div>
              <strong>{{ driverName || $t('components.tachoAnalysis.drivers.unknown') }}</strong>
              <small>{{ data.data.card_number || $t('components.tachoAnalysis.remoteFiles.noCardNumber') }}</small>
            </div>
          </template>
          <template #file-cell="{ data }">
            <div>
              <strong>{{ data.data.filename || $t('components.tachoAnalysis.remoteFiles.noFilename') }}</strong>
              <small>
                {{ formatBytes(data.data.file_size) }}<template v-if="data.data.vehicle_registration"> · {{ data.data.vehicle_registration }}</template>
              </small>
            </div>
          </template>
          <template #range-cell="{ data }">
            <div v-if="data.data.activity_from && data.data.activity_to">
              <strong>{{ formatDateFull(data.data.activity_from) }} – {{ formatDateFull(data.data.activity_to) }}</strong>
              <small>{{ data.data.days_count }} {{ $t('components.tachoAnalysis.daysUnit') }}</small>
            </div>
            <span
              v-else
              class="tfa-grid-empty"
            >{{ $t('components.tachoAnalysis.remoteFiles.rangeUnavailable') }}</span>
          </template>
          <template #downloaded-cell="{ data }">
            {{ data.data.job_completed_at ? formatDateFull(data.data.job_completed_at, true) : '—' }}
          </template>
          <template #state-cell="{ data }">
            <span
              class="tfa-pill"
              :class="statePillClass(data.data.state)"
            >{{ $t('components.tachoAnalysis.remoteFiles.states.' + data.data.state) }}</span>
          </template>
          <template #actions-cell="{ data }">
            <button
              v-if="data.data.can_import"
              type="button"
              class="tfa-btn tfa-btn--primary tfa-btn--sm tfa-remote-import"
              :disabled="importingJobId !== null"
              :aria-busy="importingJobId === data.data.job_id"
              @click="importCandidate(data.data)"
            >
              <span
                v-if="importingJobId === data.data.job_id"
                class="spinner-border spinner-border-sm"
              />
              <i
                v-else
                class="bi bi-database-add"
              />
              {{ $t(importingJobId === data.data.job_id
                ? 'components.tachoAnalysis.remoteFiles.importing'
                : 'components.tachoAnalysis.remoteFiles.import') }}
            </button>
          </template>
        </DxDataGrid>
      </div>
    </template>

    <template #footer>
      <div class="tfa-remote-footer">
        <button
          type="button"
          class="tfa-btn"
          :disabled="importingJobId !== null || refreshing"
          :aria-busy="refreshing"
          @click="refreshGrid()"
        >
          <span
            v-if="refreshing"
            class="spinner-border spinner-border-sm"
          />
          <i
            v-else
            class="bi bi-arrow-clockwise"
          />
          {{ $t('components.tachoAnalysis.remoteFiles.refresh') }}
        </button>
        <button
          type="button"
          class="tfa-btn tfa-btn--primary"
          :disabled="importingJobId !== null"
          @click="closeModal"
        >
          {{ $t('components.tachoAnalysis.remoteFiles.close') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
// "Downloaded files" of one driver: finished remote tachograph downloads (tacho_job) verified
// by the backend against the files on disk and already-imported data. Paging, sorting and
// column filtering run on the database (CustomStore + remote operations, same contract as the
// drivers grid). Importing runs the same ingest pipeline as a manual .DDD upload; the parent
// refreshes its grid on the emit. Rendered inside #tacho-file-analysis, so it shares the
// tfa-* scss and dx grid overrides.
import BaseModal from './core/BaseModal.vue'

import { defineComponent } from 'vue'
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { axiosInstance } from '@/http-common'
import { createImportCandidatesStore } from '@/stores/tachoActivityDriversStore'
import { formatDateFull } from '@/utils/tacho/format'

export default defineComponent({
  name: 'TachoRemoteFilesModal',
  components: { BaseModal, DxColumn, DxDataGrid, DxFilterRow, DxPager, DxPaging },
  emits: ['imported'],
  data(): {
    driver: any,
    candidatesStore: any,
    totalCount: number | null,
    error: string,
    importingJobId: number | null,
    refreshing: boolean
  } {
    return {
      driver: null,
      candidatesStore: null,
      totalCount: null,
      error: '',
      importingJobId: null,
      refreshing: false
    }
  },
  computed: {
    driverName(): string {
      return [this.driver?.first_name, this.driver?.last_name].filter(Boolean).join(' ')
    }
  },
  created() {
    this.candidatesStore = createImportCandidatesStore(
      () => Number(this.driver?.driver_id),
      (totalCount: number) => {
        this.totalCount = totalCount
        this.error = ''
      },
      (message: string) => {
        this.error = message || (this.$t('components.tachoAnalysis.remoteFiles.loadFailed') as string)
      },
    )
  },
  methods: {
    // The body is v-if'ed by BaseModal, so the grid remounts and loads fresh on every open.
    open(driver: any) {
      this.driver = driver
      this.totalCount = null
      this.error = ''
      const modal = this.$refs.core as any
      modal.openModal()
    },
    closeModal() {
      const modal = this.$refs.core as any
      modal.closeModal()
    },
    async refreshGrid(showStatus = true): Promise<void> {
      const instance = (this.$refs.grid as any)?.instance
      if (!instance || this.refreshing) return
      this.refreshing = true
      if (showStatus) this.totalCount = null
      try {
        await instance.refresh()
      } catch (error) {
        // DevExtreme uses this token when refresh supersedes an active grid load.
        if (error !== 'canceled') throw error
      } finally {
        this.refreshing = false
      }
    },
    filesSummary(total: number): string {
      const locale = String((this.$i18n as any).locale || 'pl')
      const category = new Intl.PluralRules(locale).select(total)
      return this.$t(`components.tachoAnalysis.remoteFiles.summary.${category}`, { total }) as string
    },
    async importCandidate(candidate: any) {
      const confirmation = await (this as any).$swal.fire({
        icon: 'question',
        title: this.$t('components.tachoAnalysis.remoteFiles.confirmTitle'),
        text: this.$t('components.tachoAnalysis.remoteFiles.confirm'),
        showCancelButton: true,
        confirmButtonText: this.$t('components.tachoAnalysis.remoteFiles.import'),
        cancelButtonText: this.$t('components.tachoAnalysis.remoteFiles.cancel'),
        customClass: { container: 'tfa-remote-confirm' },
      })
      if (!confirmation.isConfirmed) return
      this.importingJobId = candidate.job_id
      try {
        const { data } = await axiosInstance.post(
          `/api/v1/tacho/activity/driver/${this.driver.driver_id}/import-candidates/${candidate.job_id}/import`
        )
        this.$emit('imported')
        await this.refreshGrid(false).catch(() => undefined)
        this.$toast.success(this.$t(
          data?.import?.duplicate
            ? 'components.tachoAnalysis.ingest.duplicate'
            : 'components.tachoAnalysis.ingest.saved'
        ) as string)
      } catch (e: any) {
        this.$toast.error(e?.response?.data?.error || (this.$t('components.tachoAnalysis.remoteFiles.importFailed') as string))
      } finally {
        this.importingJobId = null
      }
    },
    statePillClass(state: string): string {
      switch (state) {
        case 'new': return 'tfa-pill--new'
        case 'required': return 'tfa-pill--missing'
        case 'older': return 'tfa-pill--new'
        case 'imported': return 'tfa-pill--ready'
        case 'unavailable': return 'tfa-pill--muted'
        default: return 'tfa-pill--danger'
      }
    },
    formatBytes(bytes: number | null): string {
      const b = Number(bytes || 0)
      if (!b) return this.$t('components.tachoAnalysis.remoteFiles.unknownSize') as string
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
      return `${(b / (1024 * 1024)).toFixed(1)} MB`
    },
    formatDateFull
  }
})
</script>
